import React from 'react';

import {useTranslation} from 'react-i18next';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Checkbox} from 'components/ui-kit/Checkbox';
import {HtmlText} from 'components/ui-kit/HTMLText';
import {Modal} from 'components/ui-kit/Modal';
import {DeclarationResponsibility} from 'types/gtins';

import {
    CheckboxCard,
    CheckboxLabel,
    Content,
    DeclarationCard,
    DeclarationText,
    DeclarationTitle,
    Footer,
    InfoCard,
    InfoDescription,
    InfoTitle,
    InfoWrapper,
    Loader,
    LoaderSpinner,
    WarningCard,
} from './styled';

type DeclarationResponsibilityModalProps = {
    declaration: DeclarationResponsibility | null;
    gtin?: string | number;
    isChecked: boolean;
    isLoading: boolean;
    isPending: boolean;
    onChangeChecked: (checked: boolean) => void;
    onClose: () => void;
    onConfirm: () => void;
    renderInline?: boolean;
    show: boolean;
};

export const DeclarationResponsibilityModal = ({
    declaration,
    gtin,
    isChecked,
    isLoading,
    isPending,
    onChangeChecked,
    onClose,
    onConfirm,
    renderInline = false,
    show,
}: DeclarationResponsibilityModalProps) => {
    const {t} = useTranslation();

    const declarationTitle = t('pages.registries.declarationResponsibility.title', {
        defaultValue: 'Declaration of Responsibility',
    });
    const declarationNotice = t('pages.registries.declarationResponsibility.notice', {
        defaultValue:
            'Please read the declaration carefully before confirming and sending for review.',
    });
    const declarationSignatureHint = t('pages.registries.declarationResponsibility.signatureHint', {
        defaultValue:
            'Checking the checkbox and clicking the "Sign declaration and send for review" button constitutes the user\'s simple electronic signature.',
    });
    const declarationConfirmButton = t('pages.registries.declarationResponsibility.confirmButton', {
        defaultValue: 'Sign declaration and send for review',
    });

    const declarationHtml = declaration?.text?.replace(/\n/g, '<br />') ?? '';

    const content = (
        <Content>
            {isLoading ? (
                <Loader>
                    <LoaderSpinner />
                </Loader>
            ) : (
                <InfoWrapper>
                    <InfoCard>
                        <InfoTitle>Product code: {gtin ?? '-'}</InfoTitle>
                        <InfoDescription>{declarationNotice}</InfoDescription>
                    </InfoCard>
                    <DeclarationCard>
                        <DeclarationTitle>{declarationTitle}</DeclarationTitle>
                        <DeclarationText>
                            <HtmlText __html={declarationHtml} Tag="div" />
                        </DeclarationText>
                    </DeclarationCard>
                    <WarningCard>{declarationSignatureHint}</WarningCard>
                    <CheckboxCard>
                        <Checkbox
                            checked={isChecked}
                            onChange={(event) => onChangeChecked(event.currentTarget.checked)}
                        >
                            <CheckboxLabel>{declaration?.checkboxLabel ?? ''}</CheckboxLabel>
                        </Checkbox>
                    </CheckboxCard>
                    <Footer>
                        <Button
                            variant={ButtonVariant.Outline}
                            type="button"
                            disabled={isPending}
                            onClick={onClose}
                        >
                            {t('components.form.Cancel')}
                        </Button>
                        <Button
                            variant={ButtonVariant.Normal}
                            type="button"
                            disabled={!isChecked || isPending}
                            onClick={onConfirm}
                        >
                            {declarationConfirmButton}
                        </Button>
                    </Footer>
                </InfoWrapper>
            )}
        </Content>
    );

    if (renderInline) return content;

    return (
        <Modal show={show} onClose={onClose} title={declarationTitle}>
            {content}
        </Modal>
    );
};
